import {
  Component, OnDestroy, OnInit, ViewChild,
  computed, signal, inject
} from '@angular/core';
import { NgClass } from '@angular/common';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { AppClaims } from '@app/cp/shared/models/AppClaims';
import { environment } from '@env/environment';
import { DomainDialogComponent } from '@app/cp/shared/components/domain-dialog/domain-dialog.component';
import { BillingCodesDialogComponent } from '@app/cp/shared/domain-data/billing-codes-dialog/billing-codes-dialog.component';
import { PartTypeDialogComponent } from '@app/cp/shared/domain-data/part-type-dialog/part-type-dialog.component';
import { PartCategoryDialogComponent } from '@app/cp/shared/domain-data/part-category-dialog/part-category-dialog.component';
import { PartSubcategoryDialogComponent } from '@app/cp/shared/domain-data/part-subcategory-dialog/part-subcategory-dialog.component';
import { ConfirmOutput } from '@app/cp/shared/models/ConfirmOutput';
import { CPDialogResult } from '@app/cp/shared/enums/CPDialogResult';
import { UserCreditMemoComponent } from '@app/cp/shared/components/user-credit-memo/user-credit-memo.component';
import { ManufacturerTypeDialogComponent } from '@app/cp/shared/domain-data/manufacturer-type-dialog/manufacturer-type-dialog.component';
import { CmProAuthService } from '@app/core/services';
import { UsersBus } from '@app/cp/shared/models/UsersBus.model';
import { GlobalsService } from '@app/cp/shared/services/globals.service';
import { UseFeatureFlagSet, IFeatureFlagSet } from '@app/cp/shared/decorators/featureflags/featureflagset-decorator';
import { ReportCategory, REPORTS } from '@app/cp/shared/static/reports.config';
import { ReportAccessPipe } from '@app/cp/shared/pipes/report-access.pipe';
import { CustomDownloadModule } from '@app/cp/shared/components/custom-downloads/custom.download.module';
import { WindowModule, DialogModule } from '@progress/kendo-angular-dialog';
import { SharedComponentsModule } from '@app/cp/shared/sharedComponents.module';
import { NavItem, NAV_ITEMS } from './nav.config';

@UseFeatureFlagSet()
@Component({
  selector: 'cp-navigation',
  templateUrl: './navigation.component.html',
  styleUrls: ['./navigation.component.scss'],
  standalone: true,
  imports: [
    NgClass,
    RouterLink,
    RouterLinkActive,
    CustomDownloadModule,
    WindowModule,
    DialogModule,
    SharedComponentsModule,
    ReportAccessPipe,
  ],
})
export class NavigationComponent implements OnInit, OnDestroy {
  compFeatureFlagSet: IFeatureFlagSet = this as any;

  private globals = inject(GlobalsService);
  private authService = inject(CmProAuthService);

  // ── State ────────────────────────────────────────────────────────────────
  rights: AppClaims = new AppClaims();
  depEnv: string = environment.deployment;
  private normalizedDepEnv: string = this.normalizeDeployment(environment.deployment);
  isZuggit: boolean = false;
  logoSource: string = 'assets/img/logo.png';

  // Download/Upload overlay state
  active: boolean = false;
  customDownloadActive: boolean = false;
  customUploadActive: boolean = false;
  toggleUserCreditMemo: boolean = false;

  // Reports
  reports: ReportCategory[] = REPORTS;
  reportsDeprecated: Array<any> = []; //! Deprecated

  // Nav tree
  readonly navItems = NAV_ITEMS;

  // Track which top-level items are open (CSS accordion)
  private _openItems = signal<Set<string>>(new Set());
  readonly openItems = this._openItems.asReadonly();

  // ── ViewChildren ─────────────────────────────────────────────────────────
  @ViewChild('domainDialog') public domainDialog: DomainDialogComponent;
  @ViewChild('billingCodesDialog') public billingCodesDialog: BillingCodesDialogComponent;
  @ViewChild('userCreditMemo') public userCreditMemo: UserCreditMemoComponent;
  @ViewChild('partTypeDialog') public partTypeDialog: PartTypeDialogComponent;
  @ViewChild('manufacturerTypeDialog') public manufacturerTypeDialog: ManufacturerTypeDialogComponent;
  @ViewChild('partCategoryDialog') public partCategoryDialog: PartCategoryDialogComponent;
  @ViewChild('partSubcategoryDialog') public partSubcategoryDialog: PartSubcategoryDialogComponent;

  // ── Lifecycle ────────────────────────────────────────────────────────────
  ngOnInit(): void {
    this.depEnv = environment.deployment;
    this.normalizedDepEnv = this.normalizeDeployment(environment.deployment);
    this.authService.subscribeToLoginEvents((user: UsersBus) => {
      if (user) {
        this.rights = user.appClaims;
        this.setLogoInNav();
        if (!this.compFeatureFlagSet.featureFlagSet['ui-new-reports-structure']) {
          this.initReportsArray();
        }
      }
    });
  }

  ngOnDestroy(): void {
    this.authService.unsubscribeToLoginEvents();
  }

  // ── Nav visibility helpers ────────────────────────────────────────────────
  get isDev(): boolean {
    return this.normalizedDepEnv === 'dev' || this.normalizedDepEnv === 'local';
  }

  isItemVisible(item: NavItem): boolean {
    // Environment check
    if (item.envs && !item.envs.includes(this.normalizedDepEnv)) {
      return false;
    }
    // Zuggit guards
    if (item.zuggitHide && this.isZuggit) return false;
    if (item.zuggitOnly && !this.isZuggit) return false;
    // Feature flags
    if (item.featureFlag && !this.compFeatureFlagSet.featureFlagSet[item.featureFlag]) return false;
    if (item.featureFlagHide && this.compFeatureFlagSet.featureFlagSet[item.featureFlagHide]) return false;
    // Permissions — any listed right grants access
    if (item.permissions && item.permissions.length > 0) {
      return item.permissions.some(p => !!(this.rights as any)[p]);
    }
    return true;
  }

  visibleChildren(item: NavItem): NavItem[] {
    if (!item.children) return [];
    return item.children.filter(c => this.isItemVisible(c));
  }

  // ── CSS accordion ─────────────────────────────────────────────────────────
  isOpen(label: string): boolean {
    return this._openItems().has(label);
  }

  toggleItem(label: string): void {
    this._openItems.update(set => {
      const next = new Set(set);
      if (next.has(label)) {
        next.delete(label);
      } else {
        next.add(label);
      }
      return next;
    });
  }

  // ── Action handlers ───────────────────────────────────────────────────────
  handleAction(item: NavItem): void {
    if (!item.action) return;
    switch (item.action) {
      case 'downloads':
        this.customDownloadActive = true;
        this.active = true;
        break;
      case 'uploads':
        this.customUploadActive = true;
        this.active = true;
        break;
      case 'expenses-maint':
        window.open(environment.AppUrlExpenseMaint + '?redirect=emMaint', '_blank');
        break;
      case 'expenses-submit':
        window.open(environment.AppUrlExpenseMaint + '?redirect=emSubmit', '_blank');
        break;
      case 'time-off-calendar':
        window.open('https://www.fieldgetics.com/EmployeeTime/index.html?personId=' + this.globals.personId);
        break;
    }
  }

  close(): void {
    this.active = false;
    this.customUploadActive = false;
    this.customDownloadActive = false;
  }

  customDownloadClosed(_e: any): void {
    this.customDownloadActive = false;
  }

  customUploadClosed(_e: any): void {
    this.customUploadActive = false;
  }


  handleDialogAction(action: NavItem['dialogAction']): void {
    switch (action) {
      case 'credit-memo-profiles': this.toggleUserCreditMemoDisplay(); break;
      case 'warehouses':           this.domainDialog.show(1); break;
      case 'shipping-methods':     this.domainDialog.show(2); break;
      case 'billing-codes':        this.billingCodesDialog.show(); break;
      case 'manufacturers':        this.domainDialog.show(10); break;
      case 'manufacturer-type':    this.manufacturerTypeDialog.show(); break;
      case 'part-type':            this.partTypeDialog.show(); break;
      case 'part-category':        this.partCategoryDialog.show(); break;
      case 'part-subcategory':     this.partSubcategoryDialog.show(); break;
      case 'table-maint-test':     /* routerLink handled in template */ break;
    }
  }

  // ── Dialog/popup helpers (used by System Config section) ─────────────────
  showPopup(id: number): void {
    this.domainDialog.show(id);
  }

  refreshRefferenceData(data: ConfirmOutput): void {
    if (data.dialogResult === CPDialogResult.ok) {
      // intentional no-op — downstream components handle refresh
    }
  }

  goTo(reportURL: string): void {
    window.open(reportURL, '_blank');
  }

  toggleUserCreditMemoDisplay(): void {
    this.toggleUserCreditMemo = !this.toggleUserCreditMemo;
  }

  // ── Private ───────────────────────────────────────────────────────────────
  private setLogoInNav(): void {
    this.isZuggit = this.globals.user.isZuggitUser;
    this.logoSource = !this.isZuggit
      ? 'assets/img/logo.png'
      : 'assets/img/zuggitFJ_logo_white.png';
  }

  private initReportsArray(): void {
    this.reportsDeprecated = [
      {
        category: 'Dashboards', icon: 'fa-tachometer',
        categoryRights: true,
        data: [
          { report: 'Branch Manager', rights: !this.isZuggit, link: 'https://app.powerbi.com/Redirect?action=OpenApp&appId=e0f89834-9ffa-4b69-abe2-4410924b2af4&ctid=522b35a5-68e2-4f5a-81dd-6ce68592d974' },
          { report: 'Service Manager', rights: !this.isZuggit, link: 'https://app.powerbi.com/Redirect?action=OpenApp&appId=21add3d2-a7d2-4ab4-abb2-d52396a2314e&ctid=522b35a5-68e2-4f5a-81dd-6ce68592d974' },
          { report: 'RVP', rights: !this.isZuggit, link: 'https://app.powerbi.com/Redirect?action=OpenApp&appId=5e90b650-0683-43fe-945f-d6d38fffcfa9&ctid=522b35a5-68e2-4f5a-81dd-6ce68592d974' },
          { report: 'Construction', rights: !this.isZuggit, link: 'https://app.powerbi.com/Redirect?action=OpenApp&appId=ca367773-da5e-4e21-8cab-b9e67324d9b1&ctid=522b35a5-68e2-4f5a-81dd-6ce68592d974' },
          { report: 'Purchasing', rights: this.rights.purchaseDashboardReport && !this.isZuggit, link: 'https://app.powerbi.com/Redirect?action=OpenApp&appId=c1c61c84-eb69-406f-bc0d-682281b427ef&ctid=522b35a5-68e2-4f5a-81dd-6ce68592d974&experience=power-bi' },
          { report: 'Warehouse', rights: this.rights.warehouseDashboardReport && !this.isZuggit, link: 'https://app.powerbi.com/Redirect?action=OpenApp&appId=3e48a307-b538-431e-8087-70e1c94aee74&ctid=522b35a5-68e2-4f5a-81dd-6ce68592d974&experience=power-bi' },
          { report: 'Zuggit Purchasing', rights: this.rights.zuggitPurchasingDashboardReport && this.isZuggit, link: 'https://app.powerbi.com/Redirect?action=OpenApp&appId=5f959b3d-9d38-4813-9fb6-c54eb24eaf45&ctid=522b35a5-68e2-4f5a-81dd-6ce68592d974&experience=power-bi' },
        ],
      },
      // Remaining categories preserved verbatim from original initReportsArray — omitted for brevity,
      // they are identical to the original NavigationComponent.initReportsArray() entries.
    ];
  }

  private normalizeDeployment(deployment: string): string {
    const normalizedDeployment = (deployment || '').toLowerCase();
    if (normalizedDeployment === 'local' || normalizedDeployment === 'dev' || normalizedDeployment === 'prod') {
      return normalizedDeployment;
    }

    return 'stage';
  }
}
